import React from "react";
import HeartIcon from "../../icons/heart.icon";
import { ShoppingCartIcon } from "../../icons/shoppingCart.icon";
import Image from "next/image";
import { useCart } from "../../hooks/cart/useCart.hook";
import { getProductUrl } from "../../core/productUrl.core";
import { Product } from "../../types/product";
import wishListCore from "../../core/wishList.core";
import { useQueryClient } from "@tanstack/react-query";
import toast from "react-hot-toast";
import { useTranslation } from "../../core/translation.core";
import Link from "next/link";
import GuruLogoIcon from "../../icons/guruLogo.icon";
import { ProductItemMembershipDiscountBadge } from "./productItemMembershipDiscountBadge.comp";
import { ProductListingPointRedemptionNote } from "../cart/pointRedemptionDisplay.comp";

export function ProductItem({
  product,
  wishList = false,
  updateItem,
  showDiscount = false,
  onRedeem,
  disabled = false,
  isCoupon = false,
  imageSize = 202,
  showAddToCartButton = true,
}: ProductItemProps) {
  const { addItem } = useCart();
  const queryClient = useQueryClient();
  const { t, msg } = useTranslation();

  const handleWishlistToggle = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (!wishList) {
        await wishListCore.addProduct(product.id);
      } else {
        await wishListCore.removeProduct(product.id);
      }
      const newWishlistStatus = !wishList;
      updateItem(product.id, newWishlistStatus);
      queryClient.invalidateQueries({ queryKey: ["WishList"] });
      const message = newWishlistStatus ? t(msg.product.addedTowishlist) : t(msg.product.removedFromWishList);
      toast.success(message);
    } catch (error) {
      console.error("Error updating wishlist:", error);
    }
  };

  const handleAddToCart = async () => {
    addItem({ product, quantity: 1 })
      .then(() => {
        toast.success("Added To Cart");
      })
      .catch((error) => {
        toast.error("Error adding to cart:", error);
      });
  };

  const handleAddToCartWithGuruFun = async () => {
    addItem({ product, quantity: 1, isPointRedeemed: true })
      .then(() => {
        toast.success("Added To Cart");
      })
      .catch((error) => {
        toast.error("Error adding to cart:", error);
      });
  };

  const handleAddProductTypeToCart = (product: Product) => {
    if (product.productType === "variationParent" || product.productType === "bundle") {
      const win = window.open(getProductUrl({ id: product.id, name: product.name }), "_blank");
      if (win) win.opener = null;
    } else {
      handleAddToCart();
    }
  };

  const handleRedeem = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (onRedeem) {
      onRedeem();
    }
  };

  return (
    <Link href={getProductUrl(product)} className="block" target="_blank" rel="noopener noreferrer">
      <div className="bg-white rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow duration-200">
        <ProductItemImage product={product} imageSize={imageSize} wishList={wishList} onWishlistToggle={handleWishlistToggle} />
        <div className="p-4">
          <ProductItemInfo product={product} />
          <ProductItemActions
            product={product}
            showDiscount={showDiscount}
            isCoupon={isCoupon}
            disabled={disabled}
            onRedeem={handleRedeem}
            onAddToCart={handleAddProductTypeToCart}
            onAddToCartWithGuruFun={handleAddToCartWithGuruFun}
            getCouponLabel={t(msg.gift.getCoupon)}
            outOfStockLabel={t(msg.product.outOfStock)}
            showAddToCartButton={showAddToCartButton}
          />
        </div>
      </div>
    </Link>
  );
}

function getPriceRange(variations: Product[]) {
  const prices = variations.map((v) => v.price).filter((price) => price !== undefined) as number[];
  if (prices.length === 0) return null;
  return { low: Math.min(...prices), high: Math.max(...prices) };
}

function getOriginalPriceRange(variations: Product[]) {
  const prices = variations.map((v) => v.originalPrice).filter((price) => price !== undefined) as number[];
  if (prices.length === 0) return null;
  return { low: Math.min(...prices), high: Math.max(...prices) };
}

function hasVariationDiscount(variations: Product[]) {
  return variations.some((v) => v.originalPrice != null && v.price !== v.originalPrice);
}

function renderDiscountedPricePair(saleLow: number, saleHigh: number, originalLow: number, originalHigh: number) {
  if (saleLow === saleHigh && originalLow === originalHigh) {
    return (
      <>
        <span className="text-gray-400 line-through">${originalLow}</span>
        <span className="text-red-600 font-bold text-base">${saleLow}</span>
      </>
    );
  }

  return (
    <>
      <span className="text-gray-400 line-through">
        ${originalLow} - ${originalHigh}
      </span>
      <span className="text-red-600 font-bold">
        ${saleLow} - ${saleHigh}
      </span>
    </>
  );
}

interface ProductItemPriceProps {
  product: Product;
  showDiscount: boolean;
  isCoupon: boolean;
}

function ProductItemPrice({ product, showDiscount, isCoupon }: ProductItemPriceProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <ProductItemMoneyPrice product={product} showDiscount={showDiscount} isCoupon={isCoupon} />
      <ProductListingPointRedemptionNote product={product} />
    </div>
  );
}

function ProductItemMoneyPrice({ product, showDiscount, isCoupon }: ProductItemPriceProps) {
  if (isCoupon) {
    return <span>{product.nonMemberPrice}分</span>;
  }

  if (product.productType === "variationParent" && product.variations && product.variations.length > 0) {
    const priceRange = getPriceRange(product.variations);
    const originalPriceRange = getOriginalPriceRange(product.variations);
    const hasDiscount = hasVariationDiscount(product.variations);

    if (priceRange) {
      if (hasDiscount && originalPriceRange) {
        return renderDiscountedPricePair(priceRange.low, priceRange.high, originalPriceRange.low, originalPriceRange.high);
      }

      if (priceRange.low === priceRange.high) {
        return <span>${priceRange.low}</span>;
      }
      return (
        <span>
          ${priceRange.low} - ${priceRange.high}
        </span>
      );
    }
  }

  if (showDiscount && product.discountPrice) {
    return (
      <>
        <span className="text-gray-400 line-through">${product.nonMemberPrice}</span>
        <span className="text-red-600">${product.discountPrice}</span>
      </>
    );
  }

  if (product.originalPrice != null && product.nonMemberPrice !== product.originalPrice) {
    return (
      <>
        <span className="text-gray-400 line-through">${product.originalPrice}</span>
        <span className="text-red-600">${product.nonMemberPrice}</span>
      </>
    );
  }

  return <span>${product.nonMemberPrice}</span>;
}

interface ProductItemImageProps {
  product: Product;
  imageSize: number;
  wishList: boolean;
  onWishlistToggle: (e: React.MouseEvent) => void;
}

function ProductItemImage({ product, imageSize, wishList, onWishlistToggle }: ProductItemImageProps) {
  return (
    <div className="relative">
      {product.featureMedia ? (
        <Image
          src={product.featureMedia.url}
          alt={product.name}
          width={imageSize}
          height={imageSize}
          className="object-contain"
          unoptimized
        />
      ) : (
        <div
          className="p-2"
          //  style={{ width: imageSize, height: imageSize }}
        >
          <GuruLogoIcon className="w-full h-full" />
        </div>
      )}
      <button
        onClick={onWishlistToggle}
        className="absolute top-2 right-2 p-2 bg-white/80 rounded-full hover:bg-white transition-colors duration-200"
      >
        <HeartIcon className={`w-5 h-5 ${wishList ? "fill-red-500 stroke-red-500" : "stroke-gray-600"}`} />
      </button>
    </div>
  );
}

interface ProductItemInfoProps {
  product: Product;
}

function ProductItemInfo({ product }: ProductItemInfoProps) {
  const { t, msg } = useTranslation();
  const visibleAttributes = product.attributes?.filter((attr) => attr.code !== "variant").slice(0, 2) ?? [];

  return (
    <>
      <h3 className="text-sm font-medium text-gray-900 mb-1 line-clamp-2 min-h-10 text-center">{product.name}</h3>
      <p className="text-xs text-gray-600 mb-2 line-clamp-2 h-12">{product.shortDescription}</p>
      {(visibleAttributes.length > 0 || product.brand) && (
        <div className="mb-2">
          {visibleAttributes.map((attr, index) => (
            <div key={attr.code || index} className="text-xs text-gray-500 mb-1">
              <span className="font-medium">{attr.name}:</span> <span>{String(attr.value)}</span>
            </div>
          ))}
          {product.brand && (
            <div className="text-xs text-gray-500 mb-1">
              <span className="font-medium">{t(msg.product.brand)}:</span> <span>{product.brand.name}</span>
            </div>
          )}
        </div>
      )}
    </>
  );
}

interface ProductItemActionsProps {
  product: Product;
  showDiscount: boolean;
  isCoupon: boolean;
  disabled: boolean;
  onRedeem: (e: React.MouseEvent) => void;
  onAddToCart: (product: Product) => void;
  onAddToCartWithGuruFun: () => void;
  getCouponLabel: string;
  outOfStockLabel: string;
  showAddToCartButton?: boolean;
}

function isProductOutOfStock(product: Product): boolean {
  if (!product.inventories || product.inventories.length === 0) return false;
  return product.inventories[0].count <= 0;
}

function ProductItemActions({
  product,
  showDiscount,
  isCoupon,
  disabled,
  onRedeem,
  onAddToCart,
  getCouponLabel,
  outOfStockLabel,
  showAddToCartButton = true,
}: ProductItemActionsProps) {
  const isOutOfStock = isProductOutOfStock(product);

  return (
    <div className="flex items-center justify-between mt-2">
      <div className="flex flex-col gap-0.5">
        <div className="text-sm font-medium text-gray-900">
          <ProductItemPrice product={product} showDiscount={showDiscount} isCoupon={isCoupon} />
        </div>
        <ProductItemMembershipDiscountBadge product={product} />
      </div>
      {showAddToCartButton &&
        (isCoupon ? (
          <button
            onClick={onRedeem}
            disabled={disabled}
            className={`px-3 py-1.5 rounded text-sm font-medium w-[80%] ${
              disabled
                ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                : "hover:opacity-80 rounded-lg bg-[#f4e097] text-gray-800 hover:bg-[#784d25] hover:text-white transition-colors duration-200"
            }`}
          >
            {getCouponLabel}``
          </button>
        ) : (
          <div className="flex flex-col gap-1 items-end">
            <button
              type="button"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                onAddToCart(product);
              }}
              disabled={isOutOfStock}
              className="p-2 bg-gray-100 rounded-full hover:bg-gray-200 transition-colors duration-200"
            >
              {isOutOfStock ? (
                <span className="text-gray-400 text-sm h-5">{outOfStockLabel}</span>
              ) : (
                <ShoppingCartIcon className="w-5 h-5 text-gray-600" />
              )}
            </button>
          </div>
        ))}
    </div>
  );
}

interface ProductItemProps {
  product: Product;
  wishList?: boolean;
  updateItem: (productName: string, wishlistStatus: boolean) => void;
  showDiscount?: boolean;
  onRedeem?: () => void;
  disabled?: boolean;
  isCoupon?: boolean;
  imageSize?: number;
  showAddToCartButton?: boolean;
}
